import { Suspense, lazy } from 'react';
//* Navigation
import { Navigate } from 'react-router';
//* Config
import { CONFIG } from 'src/config/global-config.js';
import { pageOrderCC } from 'src/config/pageInfo/pageOrder.ts';
import { PATH_AFTER_LOGIN_CC } from 'src/config.js';
//* Guards
import AuthGuard from 'src/guards/AuthGuard.jsx';
import CCPageGuard from 'src/guards/CCPageGuard.jsx';
import ContactCenterAuthGuard from 'src/guards/ContactCenterAuthGuard.jsx';
import GuestGuard from 'src/guards/GuestGuard.jsx';
import JWTPageGuard from 'src/guards/JWTPageGuard.jsx';

import {
  PATH_CONTACT_CENTER,
  PATH_CONTACT_CENTER_SUB,
  PATH_ONLINE_SUB,
  PATH_SHARED_SUB,
} from './paths.js';
//* Components
const LoadingScreen = lazy(() => import('src/components/LoadingScreen.jsx'));
//* Layout
const ContactCenterLayout = lazy(() => import('src/layouts/ContactCenter/ContactCenterLayout.jsx'));
const NoHeaderLayout = lazy(() => import('src/layouts/NoHeaderLayout.jsx'));
const StaticPageLayout = lazy(() => import('src/layouts/StaticPageLayout.jsx'));
//* Pages
// const OpeningScriptPage = lazy(() => import('src/pages/contactCenter/OpeningScript.jsx'));
const PaymentDetailsCCPage = lazy(() => import('src/pages/contactCenter/PaymentDetailsCC.jsx'));
const PolicyOutcomeCCPage = lazy(() => import('src/pages/contactCenter/PolicyOutcomeCC.jsx'));
const LoginPage = lazy(() => import('src/pages/auth/Login.jsx'));
const ProcessingLandingPageCCPage = lazy(
  () => import('src/pages/contactCenter/ProcessingLandingPageCC.jsx'),
);
const RetrieveQuotePageCCPage = lazy(
  () => import('src/pages/contactCenter/RetrieveQuotePageCC.jsx'),
);
const UpgradeSaveConfirmationPage = lazy(
  () => import('src/pages/contactCenter/UpgradeSaveConfirmation.jsx'),
);
const LandingPagePage = lazy(() => import('src/pages/LandingPage.jsx'));
const LifeInfoAndDutyPage = lazy(() => import('src/pages/LifeInfoAndDuty.jsx'));
const GettingStartedCCPage = lazy(() => import('src/pages/contactCenter/GettingStartedCC.jsx'));

const SaveAndEmailCCPage = lazy(() => import('src/pages/contactCenter/SaveAndEmailCC.jsx'));

const AboutYouInformationPage = lazy(
  () => import('src/pages/shared/prequalification/AboutYou.jsx'),
);

const AboutYouDeclarationsPage = lazy(
  () => import('src/pages/shared/prequalification/AboutYouDeclarations.jsx'),
);

const Page404Page = lazy(() => import('src/pages/Page404.jsx'));
const PaymentContactDetailsPage = lazy(
  () => import('src/pages/shared/paymentPath/PaymentContactDetails.jsx'),
);
const PolicyPurchaseConfirmationPage = lazy(
  () => import('src/pages/shared/paymentPath/PolicyPurchaseConfirmation.jsx'),
);
const AlreadyDeclinedApplication = lazy(
  () => import('src/pages/shared/policyOutcome/AlreadyDeclinedApplication.jsx'),
);
const PolicyOutcomeDeclinePage = lazy(
  () => import('src/pages/shared/policyOutcome/PolicyOutcomeDecline.jsx'),
);
const QuoteSummaryPage = lazy(() => import('src/pages/shared/prequalification/QuoteSummary.jsx'));
const SelectCoverPage = lazy(() => import('src/pages/shared/prequalification/SelectCover.jsx'));
const YourDetailsContactPage = lazy(
  () => import('src/pages/shared/prequalification/YourDetailsContact.jsx'),
);
const RetrieveOutcomeBoundPage = lazy(
  () => import('src/pages/shared/retrieveOutcome/RetrieveOutcomeBound.jsx'),
);
const RetrieveOutcomeDeclinePage = lazy(
  () => import('src/pages/shared/retrieveOutcome/RetrieveOutcomeDecline.jsx'),
);
const RetrieveOutcomeExpiredPage = lazy(
  () => import('src/pages/shared/retrieveOutcome/RetrieveOutcomeExpired.jsx'),
);
const RetrieveLoadingPage = lazy(
  () => import('src/pages/shared/retrieveQuote/RetrieveLoading.jsx'),
);
const AboutYouDisclosurePage = lazy(
  () => import('src/pages/shared/underwriting/AboutYouDisclosure.jsx'),
);
const AboutYouQuestionsPage = lazy(
  () => import('src/pages/shared/underwriting/AboutYouQuestions.jsx'),
);
const ReviewApplicationPage = lazy(
  () => import('src/pages/shared/underwriting/ReviewApplication.jsx'),
);
//* ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<LoadingScreen isOnline={false} />}>
      <Component {...props} />
    </Suspense>
  );
};

//** ---------------------------------------------------------------------- */
// ** AUTHENTICATION
const Login = Loadable(LoginPage);

const GettingStartedCC = Loadable(GettingStartedCCPage);
const RetrieveQuotePageCC = Loadable(RetrieveQuotePageCCPage);
const ProcessingLandingPageCC = Loadable(ProcessingLandingPageCCPage);

//*********************************/
// ** CONTACT CENTER
// const OpeningScript = Loadable(OpeningScriptPage);
// ** CONTACT CENTER PAYMENTS
const PaymentDetailsCC = Loadable(PaymentDetailsCCPage);

const PolicyOutcomeCC = Loadable(PolicyOutcomeCCPage);

const UpgradeSaveConfirmation = Loadable(UpgradeSaveConfirmationPage);

//** Save and Email
const SaveAndEmailCC = Loadable(SaveAndEmailCCPage);
//*********************************/

// ** SHARED
const YourDetailsContact = Loadable(YourDetailsContactPage);
const SelectCover = Loadable(SelectCoverPage);
const QuoteSummary = Loadable(QuoteSummaryPage);
const AboutYouInformation = Loadable(AboutYouInformationPage);
const AboutYouDeclarations = Loadable(AboutYouDeclarationsPage);

// ** SHARED PAYMENT PATH
const PaymentContactDetails = Loadable(PaymentContactDetailsPage);
const PolicyPurchaseConfirmation = Loadable(PolicyPurchaseConfirmationPage);
const AboutYouDisclosure = Loadable(AboutYouDisclosurePage);
const AboutYouQuestions = Loadable(AboutYouQuestionsPage);
const PolicyOutcomeDecline = Loadable(PolicyOutcomeDeclinePage);
const ReviewApplication = Loadable(ReviewApplicationPage);
const RetrieveLoading = Loadable(RetrieveLoadingPage);
const RetrieveDecline = Loadable(RetrieveOutcomeDeclinePage);
const RetrieveReferredUW = Loadable(RetrieveOutcomeDeclinePage);
const RetrieveExpired = Loadable(RetrieveOutcomeExpiredPage);
const RetrieveBound = Loadable(RetrieveOutcomeBoundPage);
const ApplicationAlreadyDeclined = Loadable(AlreadyDeclinedApplication);
// ** miscellaneous
const NotFound = Loadable(Page404Page);
const LandingPage = Loadable(LandingPagePage);
const LifeInfoAndDuty = Loadable(LifeInfoAndDutyPage);

//** ---------------------------------------------------------------------- */

export default function RouterCC() {
  const rootRedirect = 'true' === import.meta.env.VITE_ROOT_REDIRECT;

  return [
    {
      path: 'auth',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    //** Contact Center Routes  */
    {
      path: PATH_CONTACT_CENTER_SUB.root,
      element: CONFIG.auth.skip ? (
        <ContactCenterLayout />
      ) : (
        <ContactCenterAuthGuard>
          <ContactCenterLayout />
        </ContactCenterAuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN_CC} replace />,
          index: true,
        },
        // {
        //   path: PATH_CONTACT_CENTER_SUB.openingScript,
        //   element: CONFIG.auth.skip ? (
        //     <OpeningScript pageIndex={pageOrderCC.SCRIPT} />
        //   ) : (
        //     <CCPageGuard>
        //       <OpeningScript pageIndex={pageOrderCC.SCRIPT} />
        //     </CCPageGuard>
        //   ),
        // },
        {
          path: PATH_CONTACT_CENTER_SUB.gettingStarted,
          element: CONFIG.auth.skip ? (
            <GettingStartedCC pageIndex={pageOrderCC.GETTING_STARTED} />
          ) : (
            <CCPageGuard>
              <GettingStartedCC pageIndex={pageOrderCC.GETTING_STARTED} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.cover,
          element: CONFIG.auth.skip ? (
            <SelectCover pageIndex={pageOrderCC.COVER} />
          ) : (
            <CCPageGuard>
              <SelectCover pageIndex={pageOrderCC.COVER} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.quote,
          element: CONFIG.auth.skip ? (
            <QuoteSummary pageIndex={pageOrderCC.QUOTE} />
          ) : (
            <CCPageGuard>
              <QuoteSummary pageIndex={pageOrderCC.QUOTE} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.saveAndEmail,
          element: (
            <CCPageGuard>
              <SaveAndEmailCC pageIndex={pageOrderCC.SAVE_AND_EMAIL} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.aboutYouInformation,
          element: (
            <CCPageGuard>
              <AboutYouInformation pageIndex={pageOrderCC.ABOUT_YOU_INFORMATION} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.aboutYouDeclarations,
          element: (
            <CCPageGuard>
              <AboutYouDeclarations pageIndex={pageOrderCC.ABOUT_YOU_DECLARATIONS} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.contact,
          element: CONFIG.auth.skip ? (
            <YourDetailsContact pageIndex={pageOrderCC.CONTACT} />
          ) : (
            <CCPageGuard>
              <YourDetailsContact pageIndex={pageOrderCC.CONTACT} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.aboutYouDisclosure,
          element: (
            <JWTPageGuard>
              <AboutYouDisclosure pageIndex={pageOrderCC.ABOUT_YOU_DISCLOSURE} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.aboutYouQuestions,
          element: (
            <JWTPageGuard>
              <AboutYouQuestions pageIndex={pageOrderCC.ABOUT_YOU_QUESTIONS} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.aboutYourReview,
          element: (
            <JWTPageGuard>
              <ReviewApplication pageIndex={pageOrderCC.REVIEW_APPLICATION} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.ppPolicyOutcomeDecline,
          element: (
            <JWTPageGuard>
              <PolicyOutcomeDecline pageIndex={pageOrderCC.PP_POLICY_OUTCOME_DECLINE} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.ppContact,
          element: (
            <JWTPageGuard>
              <AuthGuard>
                <PaymentContactDetails pageIndex={pageOrderCC.PP_CONTACT} />
              </AuthGuard>
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.ppCCPayment,
          element: (
            <JWTPageGuard>
              <AuthGuard>
                <PaymentDetailsCC pageIndex={pageOrderCC.PP_CC_PAYMENT} />
              </AuthGuard>
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.ppConfirmation,
          element: (
            <JWTPageGuard>
              <AuthGuard>
                <PolicyPurchaseConfirmation pageIndex={pageOrderCC.PP_CONFIRMATION} />
              </AuthGuard>
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.ppCCPolicyOutcome,
          element: (
            <JWTPageGuard>
              <AuthGuard>
                <PolicyOutcomeCC pageIndex={pageOrderCC.PP_CC_POLICY_OUTCOME} />
              </AuthGuard>
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.upgradeSaveConfirmation,
          element: (
            <CCPageGuard>
              <AuthGuard>
                <UpgradeSaveConfirmation />
              </AuthGuard>
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.retrieveQuoteLoading,
          element: (
            <CCPageGuard>
              <RetrieveLoading pageIndex={pageOrderCC.RETRIEVE_QUOTE_LOADING} />
            </CCPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.retrieveDecline,
          element: (
            <JWTPageGuard>
              <RetrieveDecline pageIndex={pageOrderCC.RETRIEVE_DECLINE} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.retrieveReferredUW,
          element: (
            <JWTPageGuard>
              <RetrieveReferredUW pageIndex={pageOrderCC.RETRIEVE_REFERRED_UW} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.retrieveExpired,
          element: (
            <JWTPageGuard>
              <RetrieveExpired pageIndex={pageOrderCC.RETRIEVE_EXPIRED} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_CONTACT_CENTER_SUB.retrieveBound,
          element: (
            <JWTPageGuard>
              <RetrieveBound pageIndex={pageOrderCC.RETRIEVE_BOUND} />
            </JWTPageGuard>
          ),
        },
        {
          path: PATH_ONLINE_SUB.applicationAlreadyDeclined,
          element: (
            <CCPageGuard>
              <ApplicationAlreadyDeclined pageIndex={pageOrderCC.APPLICATION_ALREADY_DECLINED} />
            </CCPageGuard>
          ),
        },
      ],
    },
    {
      path: PATH_SHARED_SUB.infoAndDuty,
      element: <StaticPageLayout />,
      children: [{ element: <LifeInfoAndDuty />, index: true }],
    },
    {
      path: '/',
      ...(rootRedirect
        ? {
            element: <Navigate to={PATH_ONLINE_SUB.root} replace />,
          }
        : {
            element: <NoHeaderLayout />,
            children: [{ element: <LandingPage />, index: true }],
          }),
    },

    //* CC landing page to start or retrieve quote
    {
      path: PATH_CONTACT_CENTER.landingPage,
      element: <StaticPageLayout />,
      children: [
        {
          element: CONFIG.auth.skip ? (
            <ProcessingLandingPageCC />
          ) : (
            <ContactCenterAuthGuard>
              <ProcessingLandingPageCC />
            </ContactCenterAuthGuard>
          ),
          index: true,
        },
      ],
    },
    //* CC retrieve quote page
    {
      path: PATH_CONTACT_CENTER.retrieveQuote,
      element: <StaticPageLayout />,
      children: [
        {
          element: CONFIG.auth.skip ? (
            <RetrieveQuotePageCC />
          ) : (
            <ContactCenterAuthGuard>
              <RetrieveQuotePageCC />
            </ContactCenterAuthGuard>
          ),
          index: true,
        },
      ],
    },
    //* Main Routes
    {
      path: '*',
      element: <NoHeaderLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ];
}

//* ----------------------------------------------------------------
